<template>
  <div class="subcategory product-page" :class="pageTypeClass">
    <banner v-if="!$route.params.product && bannerImg" :imageSrc="bannerImg" />
    <navigation :subcategory="subcategory || undefined" :category-name="categoryName" />
    <div class="content product-page__content">
      <div class="container">
        <div class="card">
          <NuxtPage :subcategory="subcategory" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useMainStore } from '~/stores/main';
  import Navigation from '~/components/ui/Navigation.vue';
  import { privacyTypes } from '~/constants';

  definePageMeta({ middleware: ['check-products-url'] });

  const store = useMainStore();
  const route = useRoute();
  const categories = computed(() => store.categories);
  const baseUrlMedia = computed(() => store.baseUrlMedia);

  // Make route params reactive
  const currentCategory = computed(() => (Array.isArray(route.params.category) ? route.params.category[0] : route.params.category));
  const currentSubcategory = computed(() =>
    Array.isArray(route.params.subcategory) ? route.params.subcategory[0] : route.params.subcategory
  );
  const currentSubcategoryTab = computed(() =>
    Array.isArray(route.params.subcategoryTab) ? route.params.subcategoryTab[0] : route.params.subcategoryTab
  );

  function findNodeIdIndexInArray(arr: any[], nodeId: any): number {
    if (!arr) return -1;
    return arr.findIndex((item: any) => item.nodeId === nodeId);
  }

  const categoryIndex = computed(() => findNodeIdIndexInArray(categories.value, currentCategory.value));
  const categoryName = computed(() => {
    if (
      categories.value &&
      categoryIndex.value > -1 &&
      categories.value[categoryIndex.value] &&
      categories.value[categoryIndex.value].external &&
      typeof categories.value[categoryIndex.value].external === 'object'
    ) {
      return (categories.value[categoryIndex.value].external as any).title;
    } else {
      return '';
    }
  });
  const subcategory = computed(() => {
    if (categories.value && categoryIndex.value > -1 && categories.value[categoryIndex.value]) {
      const subCategoryIndex = findNodeIdIndexInArray(categories.value[categoryIndex.value].nodes || [], currentSubcategory.value);
      if (subCategoryIndex > -1) {
        return categories.value[categoryIndex.value].nodes?.[subCategoryIndex];
      }
    }
    return null;
  });

  // Page-level internal/service border on the card. Shown only on the
  // subcategory-tab overview page; the product detail page (route.params.product
  // present) carries its own intro rail, so we leave it unmarked there to avoid a
  // double indication. Reads the subcategory-tab node's publishedTo, falling back
  // to the subcategory's (the h2 heading lock reads the subcategory directly, so
  // the two can differ when a tab carries its own marking) (DOV-1488).
  const pageTypeClass = computed(() => {
    if (route.params.product) return '';
    const tab = subcategory.value?.nodes?.find(
      (n: any) => String(n.nodeId) === String(currentSubcategoryTab.value) || String(n.id) === String(currentSubcategoryTab.value)
    );
    const tabPublishedTo = Number(tab?.publishedTo);
    const publishedTo =
      tabPublishedTo === privacyTypes.INTERNAL || tabPublishedTo === privacyTypes.SERVICE
        ? tabPublishedTo
        : Number(subcategory.value?.publishedTo);
    switch (publishedTo) {
      case privacyTypes.INTERNAL:
        return 'product-page_internal';
      case privacyTypes.SERVICE:
        return 'product-page_service';
      default:
        return '';
    }
  });

  const bannerImg = computed(() => {
    // Build banner from subcategory's first image resource
    if (!subcategory.value || !subcategory.value.resources) return null;
    const img = subcategory.value.resources.find((r: any) => r.mimeType && r.mimeType.indexOf('image') > -1);
    if (!img) return null;
    return {
      url: baseUrlMedia.value + img.fileId + '/' + img.fileName,
      imageSize: '?width=1600'
    };
  });

  function setDataLayersPages() {
    // Implement logic for data layers if needed
  }

  onMounted(() => {
    setDataLayersPages();
  });
</script>
